/* src/components/Widgets.js - v5.8 - Scroll Fix & Responsive Footer */
import React from 'react';

function Widgets({ posts, user, onNavigatePost, onSearchTag, onNavigateGallery, onNavigate }) {
  
  if (!user) return null; // Widgets hidden for guests

  // 1. User Stats (Greeting Summary)
  const myPosts = posts.filter(p => p.username === user.name);
  const likedPosts = posts.filter(p => p.voters && p.voters[user.name] === 1);
  const totalLikes = myPosts.reduce((acc, curr) => acc + (curr.likes || 0), 0);

  // 2. Trending Tags
  const tagCounts = {};
  posts.forEach(p => {
      const tags = p.message?.match(/#\w+/g) || [];
      tags.forEach(t => {
          const tag = t.toLowerCase();
          tagCounts[tag] = (tagCounts[tag] || 0) + 1;
      });
  });
  
  const trendingTags = Object.entries(tagCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(entry => entry[0]);

  // 3. Friends Activity Logic
  const friendActivity = posts
      .filter(p => user.friends && user.friends.includes(p.username))
      .slice(0, 5); // Top 5 recent posts from friends

  // 4. My Photos Logic
  const myPhotos = myPosts
      .filter(p => p.image)
      .slice(0, 6);

  // --- REUSABLE HEADER STYLE ---
  const headerStyle = {
      margin: '0 0 15px 0',
      borderBottom: '1px solid rgba(255,255,255,0.1)',
      paddingBottom: '10px'
  };

  return (
    <div className="widgets-container">
      
      {/* 1. GREETING SUMMARY */}
      <div className="card widget-greeting" style={{ padding: '20px', borderRadius: '15px' }}>
        <h3 style={headerStyle}>
            👋 Hello, {user.name}
        </h3>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: '#ccc' }}>
            <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: 'bold', fontSize: '1.2rem', color: '#2ed573' }}>{myPosts.length}</div>
                <div>Posts</div>
            </div>
            <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: 'bold', fontSize: '1.2rem', color: '#ff4757' }}>{totalLikes}</div>
                <div>Likes</div>
            </div>
            <div style={{ textAlign: 'center' }}>
                <div style={{ fontWeight: 'bold', fontSize: '1.2rem', color: '#1e90ff' }}>{likedPosts.length}</div>
                <div>Liked</div>
            </div>
        </div>
      </div>

      {/* 2. TRENDING WIDGET */}
      <div className="card widget-trending" style={{ padding: '20px', borderRadius: '15px' }}>
          <h3 style={headerStyle}>🔥 Trending</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {trendingTags.length > 0 ? trendingTags.map(tag => (
                  <span 
                    key={tag} 
                    onClick={() => onSearchTag(tag.replace('#', ''))}
                    style={{ background: 'rgba(255,255,255,0.1)', padding: '5px 10px', borderRadius: '15px', fontSize: '0.85rem', cursor: 'pointer' }}
                  >
                      {tag}
                  </span>
              )) : <div style={{ color: '#aaa' }}>No trends yet.</div>}
          </div>
      </div>

      {/* 3. FRIENDS ACTIVITY WIDGET */}
      <div className="card widget-friends" style={{ padding: '20px', borderRadius: '15px' }}>
          <h3 style={headerStyle}>👥 Friend Activity</h3>
          
          <div className="friend-list">
              {friendActivity.length > 0 ? friendActivity.map(p => (
                  <div 
                    key={p.id} 
                    className="friend-row"
                    onClick={() => onNavigatePost(p.id)} 
                  >
                      <div className="widget-friend-avatar" style={{ width: '35px', height: '35px', borderRadius: '50%', background: p.color || '#555', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '0.8rem', overflow:'hidden', flexShrink: 0 }}>
                          {p.userImage ? <img src={p.userImage} alt={p.username} style={{width:'100%', height:'100%', objectFit:'cover'}} /> : (p.username ? p.username[0].toUpperCase() : '?')}
                      </div>
                      
                      <div style={{ flex: 1, overflow: 'hidden' }}>
                          <div className="friend-name" style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>{p.username || 'Unknown User'}</div>
                          <div className="friend-msg" style={{ fontSize: '0.75rem', color: '#aaa', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {p.image ? '📷 Posted a photo' : p.message}
                          </div>
                      </div>
                      <div className="friend-time" style={{ fontSize: '0.7rem', color: '#666' }}>
                          {p.timestamp ? p.timestamp.split(' ')[0] : ''}
                      </div>
                  </div>
              )) : <div style={{ color: '#aaa', textAlign: 'center', fontSize: '0.9rem' }}>No recent activity from friends.</div>}
          </div>
      </div>

      {/* 4. USER'S GALLERY WIDGET */}
      <div className="card widget-gallery" style={{ padding: '20px', borderRadius: '15px' }}>
          <h3 style={headerStyle}>🖼️ My Photos</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '5px' }}>
              {myPhotos.map(p => (
                  <div 
                    key={p.id} 
                    onClick={() => onNavigateGallery(user.name, p.id)}
                    style={{ aspectRatio: '1/1', background: `url(${p.image}) center/cover`, borderRadius: '8px', cursor: 'pointer' }} 
                  />
              ))}
              {myPhotos.length === 0 && <div style={{ color: '#aaa', gridColumn: 'span 3', textAlign: 'center' }}>You haven't posted any photos.</div>}
          </div>
      </div>

      {/* 5. LEGAL LINKS (DESKTOP ONLY) */}
      <div className="desktop-only" style={{ display: 'flex', justifyContent: 'center', gap: '15px', fontSize: '0.8rem', color: '#a0a0b0' }}>
          <span onClick={() => { onNavigate('terms'); window.scrollTo(0,0); }} style={{ cursor: 'pointer', textDecoration: 'underline' }}>Terms of Service</span>
          <span>|</span>
          <span onClick={() => { onNavigate('privacy'); window.scrollTo(0,0); }} style={{ cursor: 'pointer', textDecoration: 'underline' }}>Privacy Policy</span>
      </div>

      {/* 6. FOOTER */}
      <div className="widget-footer" style={{ textAlign: 'center', fontSize: '0.8rem', color: '#666' }}>
          Nubix Social v1.0 • © 2026
      </div>

      {/* 7. RESPONSIVE STYLES */}
      <style>{`
        .widgets-container {
            display: flex;
            flex-direction: column;
            gap: 15px;
            
            /* --- NEW: Sticky & Scroll Logic --- */
            position: sticky;
            top: 20px; /* Stays 20px from top of view */
            max-height: calc(100vh - 40px); /* Never taller than viewport */
            overflow-y: auto; /* Scroll internally if needed */
            
            /* Hide scrollbar for cleaner look */
            scrollbar-width: none; 
        }
        .widgets-container::-webkit-scrollbar {
            display: none; 
        }

        .friend-list { display: flex; flex-direction: column; gap: 12px; }
        .friend-row { display: flex; gap: 10px; align-items: center; cursor: pointer; padding-bottom: 8px; border-bottom: 1px solid rgba(255,255,255,0.05); }

        /* --- NEW: Height-Based Hiding (Condense on Short Screens) --- */
        @media (max-height: 900px) {
            .widget-gallery { display: none; }
        }
        @media (max-height: 750px) {
            .widget-trending { display: none; }
            .widget-footer { display: none; }
        }

        /* Existing Width Responsiveness */
        @media (max-width: 1250px) {
            .widgets-container { gap: 8px !important; }
            .widget-friend-avatar { display: none !important; }
            .friend-list { gap: 0px !important; }
            .friend-row { padding-bottom: 3px !important; padding-top: 3px !important; gap: 5px !important; }
            .friend-name { font-size: 0.8rem !important; margin-bottom: 0px !important; line-height: 1.1 !important; }
            .friend-msg { font-size: 0.7rem !important; line-height: 1.1 !important; }
            .friend-time { font-size: 0.65rem !important; }
        }
      `}</style>

    </div>
  );
}

export default Widgets;
